
import { FaWhatsapp } from "react-icons/fa";
import { Calendar, ArrowRight, MessageCircle } from "lucide-react";
import { GOOGLE_CALENDAR_LINK } from "@/lib/utils";
import { usePageContent } from "@/hooks/usePageContent";

export function CTASection({ className = "bg-white" }: { className?: string }) {
  const { getText, get } = usePageContent('home');

  const tagline       = getText('cta', 'tagline',         'Sua transformação começa agora');
  const title1        = getText('cta', 'title_1',         'Chega de dietas');
  const title2        = getText('cta', 'title_2',         'que não funcionam.');
  const description   = getText('cta', 'description',     'Agende sua avaliação gratuita e descubra o plano alimentar feito para o seu corpo e a sua rotina.');
  const btnPrimary    = getText('cta', 'btn_primary',     'Agendar Consulta Gratuita');
  const btnPrimaryUrl = getText('cta', 'btn_primary_url', GOOGLE_CALENDAR_LINK);
  const btnWpp        = getText('cta', 'btn_whatsapp',    'WhatsApp');
  const whatsappUrl   = getText('cta', 'whatsapp_url',    'https://wa.me/5511998874081');

  const statsRaw = get('cta', 'stats')?.content_value?.items as Array<{value: string; label: string}> | undefined;
  const stats = statsRaw ?? [
    { value: '+500', label: 'Pacientes atendidos' },
    { value: '6 anos', label: 'De experiência' },
    { value: '3', label: 'Consultórios' },
    { value: '5.0 ★', label: 'No Google' },
  ];

  return (
    <section className={`py-24 ${className}`}>
      <div className="container mx-auto px-6">
        <div className="relative bg-brand-secondary rounded-3xl overflow-hidden">
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: "radial-gradient(circle at 0% 100%, #1F5938 0%, transparent 60%), radial-gradient(circle at 100% 0%, #FFDE59 0%, transparent 50%)" }} />
          <div className="absolute top-6 right-6 w-48 h-48 border border-white/20 rounded-full" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 border border-white/15 rounded-full" />

          <div className="relative z-10 grid md:grid-cols-2 items-center gap-0">
            <div className="p-10 md:p-16">
              <p className="text-white/70 font-bold text-xs uppercase tracking-widest font-primary mb-4">{tagline}</p>
              <span className="title-special text-4xl text-white/80 block mb-1">{title1}</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white font-primary leading-tight mb-5">{title2}</h2>
              <p className="text-white/75 font-primary font-light text-lg leading-relaxed mb-8 max-w-sm">{description}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={btnPrimaryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-brand-secondary font-extrabold font-primary rounded-full px-8 py-4 text-base flex items-center justify-center gap-2 hover:scale-105 transition-all duration-200 shadow-xl"
                >
                  <Calendar className="w-5 h-5" />
                  {btnPrimary}
                </a>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-white/40 text-white font-bold font-primary rounded-full px-8 py-4 text-base flex items-center justify-center gap-2 hover:bg-white/10 transition-all duration-200"
                >
                  <FaWhatsapp className="w-5 h-5" />
                  {btnWpp}
                </a>
              </div>
            </div>

            <div className="hidden md:grid grid-cols-2 gap-px bg-white/10 m-px rounded-r-3xl overflow-hidden">
              {stats.map((stat, i) => (
                <div key={i} className="bg-white/8 hover:bg-white/12 transition-colors p-10 flex flex-col items-center justify-center text-center">
                  <p className="text-4xl font-extrabold text-white font-primary leading-none mb-2">{stat.value}</p>
                  <p className="text-white/50 text-sm font-primary">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact strip */}
        <div className="mt-4 rounded-2xl bg-brand-primary/5 border border-brand-primary/10 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800 font-primary">Ficou com dúvidas?</p>
              <p className="text-xs text-gray-500 font-primary">Nossa equipe responde rapidinho — escolha como prefere falar</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-bold font-primary rounded-xl px-5 py-2.5 transition-all duration-200 shadow-md shadow-[#25D366]/30 hover:scale-105"
            >
              <FaWhatsapp className="w-4 h-4" />
              Falar no WhatsApp
            </a>
            <a
              href="/contato"
              className="flex items-center justify-center gap-1.5 border border-brand-primary/25 text-brand-primary hover:bg-brand-primary/8 text-sm font-semibold font-primary rounded-xl px-5 py-2.5 transition-all duration-200"
            >
              Página de contato <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
